import {Component, OnDestroy, OnInit} from '@angular/core';
import {Subscription} from 'rxjs';
import {Feature, PriceBand} from '../../../../shared-utilities/models-old/datastructures';
import {FirebaseService} from '../../../../shared-utilities/services-old/firebase.service';
import {FireAuthService} from '../../../../shared-utilities/services-old/fire-auth.service';
import {StoreObject} from '../../../../shared-utilities/models-old/store/store-object';
import {RuleHumanIdEnum} from '../../../../features/user-access/models/rule-info.enum';
import {IconsEnum} from '../../../shared-icons/models/icons.enum';
import {IconColourEnum} from '../../../shared-icons/models/icons-colour.enum';
import {ModalControllerService} from '../../../../shared/shared-services/overlay/modal-controller.service';
import {SharedModalUtils} from '../../../../shared/shared-utils/shared-modal/shared-modal.utils';
import {presentSharedOverlayLoadingModal} from '../../../../shared/shared-components/components/shared-overlay-loading-modal/shared-overlay-loading-modal.component';

interface TempComponentAccess {
  priceBanding?: boolean;
}

@Component({
  selector: 'app-editors-config',
  templateUrl: './editors-config.component.html',
  styleUrls: ['./editors-config.component.scss'],
  standalone: false,
})
export class EditorsConfigComponent implements OnInit, OnDestroy {

  readonly icons = IconsEnum;
  readonly iconColourEnum = IconColourEnum;

  stores: StoreObject;
  changes: Record<string, {change: Record<number, PriceBand>; feature: Feature | 'user'; storeId?: string}>;

  readonly canClose: Record<string, () => boolean> = {};
  readonly show: Record<string, boolean> = {};

  private access: TempComponentAccess = {};
  private storesSubscription: Subscription | undefined;
  protected isSaving = false;

  constructor(
    private firestore: FirebaseService,
    private fireAuth: FireAuthService,
    private modalController: ModalControllerService,
    private readonly sharedModalUtils: SharedModalUtils,
  ) {
  }

  ngOnInit(): void {
    this.storesSubscription = this.firestore.stores.subscribe((storesObj) => {
      this.stores = storesObj;
      this.access.priceBanding =
        storesObj.order.length > 0 &&
        storesObj.order.every(
          (storeId: string) => this.fireAuth.hasAccess(storeId, {ruleID: RuleHumanIdEnum['a.']}) === true,
        );
    });
  }

  ngOnDestroy(): void {
    this.storesSubscription?.unsubscribe();
  }

  setChanges = <TChange extends Record<number, PriceBand>>(document: string, change: TChange, storeId?: string, feature: Feature | 'user' = 'shared'): void => {
    if (change) {
      if (!this.changes) {
        this.changes = {};
      }
      this.changes[document] = {change, feature, storeId};
    } else {
      if (this.changes && this.changes[document]) {
        delete this.changes[document];

        if (Object.keys(this.changes).length === 0) {
          this.changes = null;
        }
      }
    }
  };

  // setSaves2Save = (compName: string, count: number) => {
  //   if (count > 0) {
  //     if (!this.thingsToSave) { this.thingsToSave = {}; }
  //     this.thingsToSave[compName] = count;
  //   } else {
  //     if (this.thingsToSave && this.thingsToSave.hasOwnProperty(compName)) {
  //       delete this.thingsToSave[compName];
  //       if (Object.keys(this.thingsToSave).length === 0) {
  //         this.thingsToSave = null;
  //       }
  //     }
  //   }
  // };

  close = async (): Promise<void> => {
    const result = await this.sharedModalUtils.createModal({
      modalTitle: 'Close Editors Configurator',
      modalTitleIcon: IconsEnum.infoSlabCircleOutline,
      modalTitleIconColor: IconColourEnum.yellow,
      contextHeading: 'Are you sure you want to close this config window?',
      contextDescription: ['Any unsaved changes to your editors will not be saved.'],
      buttonAcceptText: 'I\'m sure',
      buttonCloseText: 'No',
      buttonAccept: true,
      buttonClose: true,
      buttonCloseCross: false,
    });
    if (result.data?.buttonPressed === 'button-accept') {
      await this.modalController.dismiss();
    }
  };

  showSection(path: string): void {
    if (this.show[path]) {
      if (!this.canClose.hasOwnProperty(path) || this.canClose[path]()) {
        delete this.show[path];
      }
    } else {
      this.show[path] = true;
    }
  }

  hasAccess(k: keyof TempComponentAccess): boolean {
    return Boolean(this.access[k]);
  }

  async save(): Promise<void> {
    if (!this.changes || this.isSaving) {
      return;
    }

    this.isSaving = true;
    const loadingModal = await presentSharedOverlayLoadingModal(
      this.modalController,
      'Saving editors configuration…',
    );
    try {
      const promises: Promise<void>[] = [];
      for (const document of Object.keys(this.changes)) {
        const {change, storeId, feature} = this.changes[document];
        if (feature === 'user') {
          promises.push(this.firestore.updateUserSingularDocuments(document, change));
        } else {
          promises.push(this.firestore.updateStoreDataDoc(document, storeId, change, feature, true));
        }
      }
      await Promise.all(promises);
      this.changes = null;
    } catch (error: unknown) {
      console.error(error);
    } finally {
      this.isSaving = false;
      await loadingModal.dismiss();
    }
  }
}
